import { writable, readable, get, derived } from 'svelte/store';
import dayjs from 'dayjs';
import { modal as modalService } from '@ctm/ui/Modal';

import _trackingEvents from './tracking/events';
import trackingMethods from './tracking/methods';
import { creditCardCodeByName } from '../util/payment';
import ErrorModal from '../components/ErrorModal/ErrorModal.svelte';


// ITINERARY
export let manageBookingsItinerary = writable(undefined);

export function hasManageBookingsItinerary(pnr) {
  const _manageBookingsItinerary = get(manageBookingsItinerary);

  if (_manageBookingsItinerary && (!pnr || pnr && _manageBookingsItinerary.Passengers[0].Pnr === pnr)) {
    return _manageBookingsItinerary;
  }

  return false;
}

// Itinerary Id types: search, manage, confirmation
export function getItineraryId(type) {
  return localStorage.getItem(`${type}:itineraryId`);
}
export function setItineraryId(type, id) {
  localStorage.setItem(`${type}:itineraryId`, id);
}
export function removeItineraryId(type) {
  localStorage.removeItem(`${type}:itineraryId`);
}

// todo: where to unsub?
const unsubscribeManageBookingsItinerary = manageBookingsItinerary.subscribe(itinerary => {
  if (itinerary) setItineraryId('manage', itinerary.Identifier);
});

// CONFIG
export const CONFIG = {
  API_VERSION: 18,
  ANALYTICS_ENABLED: window.ANALYTICS_ENABLED,
  MIXPANEL_APP_NAME: window.MIXPANEL_APP_NAME,
  MIXPANEL_TOKEN: window.MIXPANEL_TOKEN,
  MIXPANEL_DEBUG: window.MIXPANEL_DEBUG,
  MIXPANEL_ENABLED: window.MIXPANEL_ENABLED,
  API_URL: window.apiUrl,
  CLIENT_ID: window.CLIENT_ID,
  CLIENT_SECRET: window.CLIENT_SECRET,
  LB_VERSION: window.lbVersion,
  MOCK_URL: 'http://localhost:9000',
  MOCK_ACTIVE: false
};

const configs = {
  iata: {
    url: () => {
      return `${CONFIG.API_URL}api/iata`;
    },
    mock: `${CONFIG.MOCK_URL}/Iata`,
    mockActive: false,
    apiVersion: 20
  },

  iataSct: {
    url: () => {
      return `${CONFIG.API_URL}api/Itinerary/IataSearchSct`;
    },
    mock: `${CONFIG.MOCK_URL}/iataSct`,
    mockActive: false,
    apiVersion: 18
  },

  itinerary: {
    url: (params) => {
      return params && params.itineraryId ? `${CONFIG.API_URL}api/itinerary/${params.itineraryId}` : `${CONFIG.API_URL}api/itinerary`;
    },
    mock: `${CONFIG.MOCK_URL}/Itinerary`,
    mockActive: false,
    apiVersion: 21
  },

  itineraryApprovers: {
    url: (itineraryId) => {
      return `${CONFIG.API_URL}api/itinerary/${itineraryId}/approvers`;
    },
    mock: `${CONFIG.MOCK_URL}/itineraryApprovers`,
    mockActive: false,
    apiVersion: 19
  },

  itineraryCompanyReference: {
    url: (itineraryId) => {
      return `${CONFIG.API_URL}api/itinerary/${itineraryId}/companyreference`;
    },
    mock: `${CONFIG.MOCK_URL}/itineraryCompanyReference`,
    mockActive: false,
    apiVersion: 19
  },

  itineraryPassenger: {
    url: (params) => {
      return `${CONFIG.API_URL}api/itinerary/${params.itineraryId}/passenger${params.passengerId ? '/' + params.passengerId : ''}`;
    },
    mock: `${CONFIG.MOCK_URL}/itinerary/passenger`,
    mockActive: false,
    apiVersion: 20
  },

  itineraryPassengerApprovers: {
    url: (params) => {
      return `${CONFIG.API_URL}api/itinerary/${params.itineraryId}/passenger/${params.passengerId}/approvers/${params.approverId}`;
    },
    mock: `${CONFIG.MOCK_URL}/itinerary/passengerApprovers`,
    mockActive: false,
    apiVersion: 19
  },

  itineraryPassengerChange: {
    url: (params) => {
      return `${CONFIG.API_URL}api/itinerary/${params.itineraryId}/passenger/${params.passengerId}/change`;
    },
    mock: `${CONFIG.MOCK_URL}/itinerary/passenger/change`,
    mockActive: false,
    apiVersion: 20
  },

  itineraryPassengerChangeFees: {
    url: (params) => {
      return `${CONFIG.API_URL}api/itinerary/${params.itineraryId}/passenger/${params.passengerId}/change-fees`;
    },
    mock: `${CONFIG.MOCK_URL}/change-fees`,
    mockActive: false,
    apiVersion: 20
  },

  itineraryPassengerCar: {
    url: (params) => {
      return `${CONFIG.API_URL}api/itinerary/${params.itineraryId}/passenger/${params.passengerId}/car/${params.sectorId}`;
    },
    mock: `${CONFIG.MOCK_URL}/itinerary/passengerCar`,
    mockActive: false,
    apiVersion: 19
  },

  itineraryPassengerFlight: {
    url: (params, target) => {
      const url = `${CONFIG.API_URL}api/itinerary/${params.itineraryId}/passenger/${params.passengerId}/flight/${params.sectorId}`;
      return target ? `${url}/${target}` : url;
    },
    mock: `${CONFIG.MOCK_URL}/itinerary/passengerFlight`,
    mockActive: false,
    apiVersion: 19
  },

  itineraryPassengerHotel: {
    url: (params) => {
      return `${CONFIG.API_URL}api/itinerary/${params.itineraryId}/passenger/${params.passengerId}/hotel/${params.sectorId}`;
    },
    mock: `${CONFIG.MOCK_URL}/itinerary/passengerHotel`,
    mockActive: false,
    apiVersion: 19
  },

  itineraryPassengerMembership: {
    url: (params) => {
      return `${CONFIG.API_URL}api/itinerary/${params.itineraryId}/passenger/${params.passengerId}/membership`;
    },
    mock: `${CONFIG.MOCK_URL}/itinerary/passengerMembership`,
    mockActive: false,
    apiVersion: 19
  },

  itineraryPassengerRail: {
    url: (params) => {
      return `${CONFIG.API_URL}api/itinerary/${params.itineraryId}/passenger/${params.passengerId}/rail/${params.sectorId}`;
    },
    mock: `${CONFIG.MOCK_URL}/itinerary/passengerRail`,
    mockActive: false,
    apiVersion: 19
  },

  itineraryPaymentMethod: {
    url: (itineraryId) => {
      return `${CONFIG.API_URL}api/itinerary/${itineraryId}/paymentmethod`;
    },
    mock: `${CONFIG.MOCK_URL}/itinerary/paymentmethod`,
    mockActive: false,
    apiVersion: 19
  },

  loyaltyProgram: {
    url: () => {
      return `${CONFIG.API_URL}api/loyaltyprograms`;
    },
    mock: `${CONFIG.MOCK_URL}/loyaltyPrograms`,
    mockActive: false,
    apiVersion: 20
  },

  itinerarySearch: {
    url: (itineraryId) => {
      return `${CONFIG.API_URL}api/itinerary/${itineraryId}/search`;
    },
    mock: `${CONFIG.MOCK_URL}/Itinerary/search`,
    mockActive: false,
    apiVersion: 19
  },

  ticketsOnHold: {
    url: (itineraryId) => {
      return `${CONFIG.API_URL}api/itinerary/${itineraryId}/unusedflighttickets`;
    },
    mock: `${CONFIG.MOCK_URL}/unusedflighttickets`,
    mockActive: false,
    apiVersion: 19
  },

  travellers: {
    url: () => {
      return `${CONFIG.API_URL}api/travellers`;
    },
    mock: `${CONFIG.MOCK_URL}/itinerary/passengerApprovers`,
    mockActive: false,
    apiVersion: 20
  },

  travelSearch: {
    url: () => {
      return `${CONFIG.API_URL}api/travel/search`;
    },
    mock: `${CONFIG.MOCK_URL}/Travel`,
    mockActive: false,
    apiVersion: 20
  },

  itineraries: {
    url: (params) => {
      const qs = Object.keys(params).map(key => key + '=' + params[key]).join('&');
      return `${CONFIG.API_URL}api/itineraries?${qs}`;
    },
    mock: `${CONFIG.MOCK_URL}/itineraries`,
    mockActive: false,
    apiVersion: 21
  },

  addFlights: {
    url: (params) => {
      return `${CONFIG.API_URL}api/itinerary/${params.itineraryId}/passenger`
    },
    mock: `${CONFIG.MOCK_URL}/itinerary/passenger`,
    mockActive: false,
    apiVersion: 20
  },

  searchTravellers: {
    url: (params) => {
      return `${CONFIG.API_URL}api/travellers?query=${params.query}`
    },
    mockActive: false,
    apiVersion: 20
  }
};

export function apiConfig(key, params, target, isMock) {
  return {
    url: getUrl(key, params, target, isMock),
    version: getVersion(key),
    mockActive: getIsMock(key)
  }
}

function getUrl(key, params, target, isMockOverride) {
  const isMock = isMockOverride !== undefined ? isMockOverride : getIsMock(key);
  return isMock ? configs[key].mock : configs[key].url(params, target);
}

function getVersion(key) {
  return configs[key] ? configs[key].apiVersion : CONFIG.API_VERSION;
}

function getIsMock(key) {
  return CONFIG.MOCK_ACTIVE || configs[key].mockActive;
}




// TRACKING
export const trackingEvents = _trackingEvents;

export function trackingInit() {
  if (!CONFIG.MIXPANEL_ENABLED) return;

  mixpanel.init(CONFIG.MIXPANEL_TOKEN, {
    track_pageview: false
  });

  mixpanel.register({
    'Application': CONFIG.MIXPANEL_APP_NAME
  });

  if (CONFIG.MIXPANEL_DEBUG) {
    mixpanel.set_config({ debug: true });
  }
}


export function track(name, ...data) {
  if (!CONFIG.MIXPANEL_ENABLED) return;

  const trackMethod = trackingMethods[name];

  if (typeof trackMethod === 'function') {
    trackMethod(...data);
  } else {
    mixpanel.track(name);
  }
}

export function setTrackingIdentity(id) {
  if (!CONFIG.MIXPANEL_ENABLED) return;

  mixpanel.identify(id);
}

export function trackLogin() {
  if (!CONFIG.MIXPANEL_ENABLED) return;

  mixpanel.people.set_once({
    'First Login': new Date()
  });

  mixpanel.people.set({
    'Last Login': new Date()
  });

  mixpanel.people.increment({
    'Total Logins': 1
  });

  track('LOGIN', true);
}

export function trackUser(firstName, lastName = '', email = '', debtorId = '') {
  if (!CONFIG.MIXPANEL_ENABLED) return;

  if (firstName.includes('@') && !email) {
    email = firstName;
  }

  mixpanel.people.set({
    '$email': email,
    '$first_name': firstName,
    '$last_name': lastName,
    'Debtor identifier': debtorId
  });
}

export function time(name) {
  if (!CONFIG.MIXPANEL_ENABLED) return;

  mixpanel.time_event(name);
}


export function trackSearch(data) {
  if (!CONFIG.MIXPANEL_ENABLED) return;

  mixpanel.time_event(_trackingEvents.BOOKING_SUBMIT);

  trackingMethods.SEARCH(data);
}


// CHANGE BOOKINGS
export let changeBookingsItinerary = writable(undefined);
export let changeBookingsPaymentMethods = writable([]);
export let changeBookingsPaymentGroups = writable([]);
export let changeBookingsPaymentIsInProgress = writable(false);
export let changeBookingsPaymentIsComplete = writable(false);

export function addTemporaryCardToChangeBookingsPaymentGroups(temporaryCard) {
  const temporaryCardForPayment = {
    ExpiryMonth: temporaryCard.expiry.substr(0, 2),
    ExpiryYear: temporaryCard.expiry.substr(2, 4),
    Holder: temporaryCard.name,
    Number: temporaryCard.number.replace(/ /g,''),
    PaymentType: 'Card',
    SecurityCode: temporaryCard.cvc,
    SecurityCodeRequired: false,
    Type: {
      Code: creditCardCodeByName(temporaryCard.cardType),
      Value: null
    }
  };

  const _changeBookingsPaymentGroups = get(changeBookingsPaymentGroups).map((group) => {
    group.paymentMethods.push(temporaryCardForPayment);
    group.selectedPaymentMethod = group.paymentMethods[group.paymentMethods.length - 1];

    return group;
  });

  changeBookingsPaymentGroups.set(_changeBookingsPaymentGroups);
}

export function changeBookingComplete() {
  changeBookingsItinerary.set(undefined);
  changeBookingsPaymentMethods.set([]);
  changeBookingsPaymentGroups.set([]);
  changeBookingsPaymentIsInProgress.set(false);
  changeBookingsPaymentIsComplete.set(true);
  manageBookingsItinerary.set(undefined);
}

export function selectChangeBookingPaymentMethod(groupIdentifier, paymentMethod) {
  const _changeBookingsPaymentGroups = get(changeBookingsPaymentGroups).map((group) => {
    if (group.groupIdentifier === groupIdentifier) {
      group.selectedPaymentMethod = paymentMethod;
    }

    return group;
  });

  changeBookingsPaymentGroups.set(_changeBookingsPaymentGroups);
}


// SEARCH
const defaultSearch = {
  query: '',
  dateFrom: dayjs().format(),
  dateTo: dayjs().add(365, 'day').format()
};

export let bookings = writable([]);

export let bookingsSearchQuery = writable('');
export let bookingsSearchDateFrom = writable(defaultSearch.dateFrom);
export let bookingsSearchDateTo = writable(defaultSearch.dateTo);

export let bookingsCurrentPage = writable(1);
export let bookingsTotalItemsCount = writable(100);
export let bookingsPageSize = writable(20);
export let bookingsIsWaiting = writable(false);
export let bookingsActiveFilter = writable('All');
export let bookingsActiveSearchText = writable('');
export let bookingsActiveDateFrom = writable(defaultSearch.dateFrom);
export let bookingsActiveDateTo = writable(defaultSearch.dateTo);

export function resetBookingSearch() {
  bookingsSearchQuery.set('');
  bookingsSearchDateFrom.set(defaultSearch.dateFrom);
  bookingsSearchDateTo.set(defaultSearch.dateTo);
}

// BOOKINGS
export let canDoBookingsSearch = derived([bookingsSearchDateFrom, bookingsSearchDateTo], (dates) => dates[0] && dates[1]);


// USER
export let userInfo = writable(undefined);
export let userDebtor = writable(JSON.parse(localStorage.getItem('CURRENT_USER_debtor')));

userDebtor.subscribe(value => {
  if (value) {
    resetChangeBookings();
    localStorage.setItem('CURRENT_USER_debtor', JSON.stringify(value));
  } else {
    localStorage.removeItem('CURRENT_USER_debtor');
  }
});

export let UserIsConsultant = derived(userInfo, $userInfo => $userInfo && $userInfo.Role === 'Consultant');
export let isAnonymousUser = writable(false);
export let usersName = writable('');
export let usersEmail = writable('');
export let usersDebtor = writable(null);
export let usersInitials = writable('');
export let UserRequiresDebtor = derived([userDebtor, UserIsConsultant], (userSettings) => !userSettings[0] && userSettings[1]);


// LOGOUT & RESET STORES
function resetUser() {
  userInfo.set(undefined);
  userDebtor.set(undefined);
  isAnonymousUser.set(false);
  usersName.set('');
  usersEmail.set('');
  usersEmail.set('');
  usersDebtor.set(null);
  usersInitials.set('');
}

function resetChangeBookings() {
  changeBookingsItinerary.set(undefined);
  changeBookingsPaymentMethods.set([]);
  changeBookingsPaymentGroups.set([]);
  changeBookingsPaymentIsInProgress.set(false);
  changeBookingsPaymentIsComplete.set(false);
}

export function logout() {
  resetUser();
  resetChangeBookings();
  resetBookingSearch();
  bookings.set([]);
  bookingsCurrentPage.set(1);
  bookingsTotalItemsCount.set(100);
  bookingsPageSize.set(20);
  bookingsIsWaiting.set(false);
  bookingsActiveFilter.set('All');
  bookingsActiveSearchText.set('');
  bookingsActiveDateFrom.set(defaultSearch.dateFrom);
  bookingsActiveDateTo.set(defaultSearch.dateTo);
}

// SETTINGS
export let settings = writable(undefined);

// HELP PANEL
export let helpPanelShow = writable(false);

// CONSTANTS
export const CURRENT_YEAR = readable(new Date().getFullYear());
export const DISABLE_SEATMAPS = readable(false);
export const NO_POLICY_BREAK = readable('Within Policy');

export const dateFormat = readable({
  DATE_DAY: 'ddd, DD MMM',
  DATE_EXTENDED: 'ddd, DD MMM YYYY',
  DATE_EXTENDED_NO_COMMA: 'ddd DD MMM YYYY',
  DATE_TIME_FULL: 'DD MMM YYYY hh:mma',
  DATE_TIME_EXTENDED: 'ddd, DD MMM YYYY hh:mma',
  DATE_TIME_THEN_DATE: 'hh:mma - ddd, DD MMM YYYY'
});

export const NO_CHANGE_REASON_MESSAGES = {
  Default: 'Sorry, this flight is unable to be changed online. Please contact the consultant',
  None: '',
  Departed: 'This flight has already departed',
  GdsChangesNotAllowed: 'This airline doesn\'t allow online changes. To make changes contact your dedicated travel consultant',
  OutsideBookingLeadTime: '',
  ChangesNotPermittedWithinTimeFrame: '',
  CarrierChangesNotAllowed: '',
  ProviderChangesNotAllowed: '',
  CountryPairChangesNotAllowed: '',
  MarriedSegmentHasDeparted: '',
  SegmentHasDeparted: '',
  CodeShare: '',
  ErrorRetrievingGdsOrder: '',
  Unticketed: ''
}


// MODAL
export function showErrorModal(messages = []) {
  modalService.open(ErrorModal,
    {
      message: messages.join('')
    }
  );
}

// HELP PANEL
export let helpPanelOpen = writable(false);
export let helpPanelContentKey = writable(undefined); // searchForm, flightResults, hotelResults, carResults,


// FARE CLASSES
export function orderFareClasses(fareClasses) {
  const buckets = window.settings.FlightConfiguration.FareBucketClasses;

  fareClasses = fareClasses.map(fareClass => {
    let bucket = buckets.find(bucket => bucket.FareClasses.indexOf(fareClass)) > -1;
    return { name: fareClass, sort: bucket.Sort};
  });

  return fareClasses.sort((a, b) => {
    a.Sort - b.Sort
  }).map(fareClass => fareClass.name);
}


// AIRPORT TRANSFERS 
export let airportTransferSelections = writable(null);
export let airportTransferItineraryId = writable(null);
export let airportTransferShowConfirmation = writable(false);
export let airportTransferItineraryData = writable(null);
export let airportTransferBooked = writable([]);
export let airportTransferError = writable(false);

export function airportTransferReset() {
  airportTransferSelections.set(null);
  airportTransferItineraryId.set(null);
  airportTransferShowConfirmation.set(false);
  airportTransferItineraryData.set(null);
  airportTransferBooked.set([]);
  airportTransferError.set(false);
}

export function airportTransfersPrepData() {
  let transfers = get(airportTransferSelections);
  let itinerary = get(airportTransferItineraryData)

  let payload = [];

  Object.keys(transfers).forEach(key => {
    let sectorId = key.split('_')[0];
    let sectorDetails = itinerary.Passengers[0].Flights.find(flight => flight.Identifier === sectorId);
    let transfer = transfers[key];
    
    const {
      TravellerFirstName,
      TravellerLastName,
      Reference,
      ServiceTime,
      Gst,
      ServiceType,
      LocationCode,
      Pax,
      PaymentType,
      ServiceLevel,
      RetailFare,
      FareType,
      ReservationStatus
    } = transfer.data;

    const {
      PrimaryContactPassengerIdentifier,
      PassengerIdentifiers,
      TravellerTitle,
      TravellerEmail,
      TravellerPhone,
      PickupTime,
      DropoffTime,
      AdditionalBags,
      Bikes,
      GolfClubs,
      SkiSnowboards,
      Surfboards,
      OtherOversized,
      Notes,
      PromoCode,
    } = transfer.metadata;


    let item = {
      PassengerIdentifier: PrimaryContactPassengerIdentifier,
      PrimaryContactPassengerIdentifier,
      PassengerIdentifiers,
      TravellerFirstName,
      TravellerSurname: TravellerLastName,
      TravellerTitle,
      TravellerEmail,
      TravellerPhone,
      Reference,
      ServiceTime,
      PickupTime,
      DropoffTime,
      PickupAddress: transfer.data.PickupAddress,
      DropoffAddress: transfer.data.DropoffAddress,
      TotalFare: RetailFare,
      Gst,
      ServiceType,
      LocationCode,
      Pax,
      PaymentType,
      ServiceLevel,
      RetailFare,
      FareType,
      ReservationStatus,
      ExtraItems: AdditionalBags + Bikes + GolfClubs + SkiSnowboards + Surfboards + OtherOversized,
      AdditionalBags,
      Bikes,
      GolfClubs,
      SkiSnowboards,
      Surfboards,
      OtherOversized,
      Notes,
      PromoCode,
      ServiceCode: sectorDetails.Carrier + sectorDetails.FlightNumber,
      Airline: sectorDetails.Carrier,
    };

    payload.push(item);
  })

  return {
    itineraryId: get(airportTransferItineraryId),
    bodyData: payload
  };
}
